import { Modal } from '@citizen-walk/shared-ui';
import {
  isAnnouncementVisible,
  useAnnouncements,
  type AnnouncementLevel,
} from './useAnnouncements';

/**
 * pattern='modal' 型お知らせを 1 件のみ表示するブロッキング Modal。
 * 旧 AnnouncementHost.tsx (L3 参照) の modal 部分を踏襲。
 *
 * 旧との差分:
 * - shared-ui Modal 経由 (overlay click では閉じず、確認ボタン必須)
 * - forceReshow=true 時は seenIds を無視して再表示 (admin 第 15 弾、REQ-4.3.33)
 *
 * level → badge 色:
 *   info     → info-600 (青)
 *   warning  → warning-600 (橙)
 *   critical → danger-700 (赤)
 */
const LEVEL_BG: Record<AnnouncementLevel, string> = {
  info: 'bg-info-600',
  warning: 'bg-warning-600',
  critical: 'bg-danger-700',
};

const LEVEL_LABEL: Record<AnnouncementLevel, string> = {
  info: '情報',
  warning: '注意',
  critical: '重要',
};

export interface AnnouncementModalProps {
  /**
   * 現在地区 ID。この地区の announcements を購読する (必須)。
   * 省略すると fallback phase2-default を読み、実地区のお知らせが配信されない不具合になる。
   */
  districtId: string;
  /**
   * 現セッション ID。Announcement.targetSessionIds の filter に渡す (user 第 2 弾、REQ-4.3.33)。
   * null = 未選択 (targetSessionIds 非空時は弾かれて表示されない)。
   */
  currentSessionId?: string | null;
}

export function AnnouncementModal({ districtId, currentSessionId = null }: AnnouncementModalProps) {
  const { state, seenIds, markSeen } = useAnnouncements(districtId);

  if (state.status !== 'ready') return null;

  const item = state.items.find(
    (a) => a.pattern === 'modal' && isAnnouncementVisible(a, seenIds, new Date(), currentSessionId),
  );
  if (!item) return null;

  // shared-ui Modal の `×` 閉じるボタンは無効化できないため、押下時も markSeen を呼ぶ
  // (旧 AnnouncementHost.tsx は × 無しで「確認しました」必須だったが、a11y (Esc) 対応のため
  //  shared-ui Modal 採用、結果として × 押下 ≒ 確認扱いに緩和。docs/97 §3 引継ぎ参照)。
  return (
    <Modal
      open={true}
      onClose={() => markSeen(item.id)}
      closeOnOverlayClick={false}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <span
            className={`${LEVEL_BG[item.level]} text-white text-xs px-2 py-0.5 rounded`}
            aria-label={`レベル: ${LEVEL_LABEL[item.level]}`}
          >
            {LEVEL_LABEL[item.level]}
          </span>
          <span>📣 {item.title}</span>
        </span>
      }
    >
      <p className="whitespace-pre-wrap text-neutral-800 mb-6">{item.body}</p>
      <button
        type="button"
        onClick={() => markSeen(item.id)}
        className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded font-medium min-h-touch"
      >
        確認しました
      </button>
    </Modal>
  );
}
